import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { PasswordField } from '../../components/ui/PasswordField'

interface BannerFile {
  id: number
  path: string
  filename: string
  ext: string
  sizeBytes: string
  modifiedAt: string | null
  parsedWidth: number | null
  parsedHeight: number | null
  parsedProduct: string | null
  parsedTags: string[] | null
}

interface SearchResp {
  files: BannerFile[]
  total: number
}

interface NextcloudSettings {
  webdavUrl: string | null
  username: string | null
  passwordSet: boolean
  rootPath: string
  deconturiBasePath: string
  deconturiShareToken: string | null
  enabled: boolean
  lastSyncAt: string | null
  lastSyncCount: number | null
}

interface DimensionStat {
  width: number
  height: number
  count: number
}

const PRODUCTS = [
  { value: '', label: 'Toate' },
  { value: 'magneziu', label: 'Magneziu Bisglicinat' },
  { value: 'magtein', label: 'Magtein (L-Threonate)' },
  { value: 'berberine', label: 'Berberine' },
  { value: 'fier', label: 'Fier Bisglicinat' },
  { value: 'zinc', label: 'Zinc Bisglicinat' },
  { value: 'ashwagandha', label: 'Ashwagandha' },
  { value: 'vitaminad3', label: 'Vitamina D3' },
  { value: 'vitaminad3k2', label: 'Vitamina D3+K2' },
  { value: 'power_v8', label: 'Power V8' },
  { value: 'procontrol', label: 'ProControl' },
  { value: 'prointense', label: 'ProIntense' },
]

export function BannerHelperPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.materials:write')
  // Contul Nextcloud e al firmei si deschide si DECONTURI: setarile le schimba doar adminul.
  const isAdmin = useAuthStore((s) => s.user?.role) === 'admin'

  const [width, setWidth] = useState('2280')
  const [height, setHeight] = useState('660')
  const [tolerance, setTolerance] = useState('0')
  const [product, setProduct] = useState('')
  const [q, setQ] = useState('')
  const [ext, setExt] = useState('')
  const [tag, setTag] = useState('')

  const settingsQuery = useQuery<{ settings: NextcloudSettings | null }>({
    queryKey: ['nextcloud-settings'],
    queryFn: async () =>
      (await api.get<{ settings: NextcloudSettings | null }>('/marketing/banner-archive/settings'))
        .data,
  })

  const dimensionsQuery = useQuery<{ dimensions: DimensionStat[] }>({
    queryKey: ['banner-dimensions'],
    queryFn: async () =>
      (await api.get<{ dimensions: DimensionStat[] }>('/marketing/banner-archive/dimensions')).data,
  })

  const searchQuery = useQuery<SearchResp>({
    queryKey: ['banner-search', { width, height, tolerance, product, q, ext, tag }],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (width) params.set('width', width)
      if (height) params.set('height', height)
      if (tolerance && Number(tolerance) > 0) params.set('tolerance', tolerance)
      if (product) params.set('product', product)
      if (q.trim()) params.set('q', q.trim())
      if (ext) params.set('ext', ext)
      if (tag) params.set('tag', tag)
      params.set('limit', '60')
      return (await api.get<SearchResp>(`/marketing/banner-archive/search?${params}`)).data
    },
  })

  // Settings form
  const [s_webdavUrl, setSWebdavUrl] = useState('')
  const [s_username, setSUsername] = useState('')
  const [s_password, setSPassword] = useState('')
  const [s_rootPath, setSRootPath] = useState('04_Marketing ecosystem')
  const [s_deconturiBasePath, setSDeconturiBasePath] = useState('DECONTURI')
  const [s_deconturiShareToken, setSDeconturiShareToken] = useState('')
  const [s_enabled, setSEnabled] = useState(false)
  const [showSettings, setShowSettings] = useState(false)

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { enabled: s_enabled, rootPath: s_rootPath }
      if (s_webdavUrl.trim()) body.webdavUrl = s_webdavUrl.trim()
      if (s_username.trim()) body.username = s_username.trim()
      if (s_password.trim()) body.password = s_password.trim()
      if (s_deconturiBasePath.trim()) body.deconturiBasePath = s_deconturiBasePath.trim()
      // trimis mereu (si gol) — gol = revenire la mod cont WebDAV normal
      body.deconturiShareToken = s_deconturiShareToken.trim()
      return (await api.put('/marketing/banner-archive/settings', body)).data
    },
    onSuccess: () => {
      toast.success('Setări salvate')
      queryClient.invalidateQueries({ queryKey: ['nextcloud-settings'] })
      setSPassword('')
    },
    onError: (err: unknown) => {
      const m =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare'
      toast.error(m)
    },
  })

  const testMutation = useMutation({
    mutationFn: async () => (await api.post('/marketing/banner-archive/test-connection')).data,
    onSuccess: (d: unknown) => {
      const r = d as { ok: boolean; itemsAtRoot?: number; error?: string }
      if (r.ok)
        toast.success(`Conexiune reușită (${r.itemsAtRoot ?? 0} elemente în folderul rădăcină)`)
      else toast.error(r.error ?? 'Eroare')
    },
    onError: () => toast.error('Eroare conexiune'),
  })

  const indexMutation = useMutation({
    mutationFn: async () => (await api.post('/marketing/banner-archive/index-now')).data,
    onSuccess: () => {
      toast.success('Indexarea a pornit în fundal. Durează 1–5 minute; apoi reîncarcă pagina.')
    },
    onError: () => toast.error('Eroare la pornirea indexării'),
  })

  const settings = settingsQuery.data?.settings
  const lastSync = settings?.lastSyncAt ? new Date(settings.lastSyncAt) : null

  // Prepopuleaza setarile deconturilor din valorile salvate
  useEffect(() => {
    if (settings?.deconturiBasePath) setSDeconturiBasePath(settings.deconturiBasePath)
    if (settings?.deconturiShareToken) setSDeconturiShareToken(settings.deconturiShareToken)
  }, [settings?.deconturiBasePath, settings?.deconturiShareToken])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Marketing → Banner Helper</h1>
        <div className="flex gap-2">
          {canWrite && (
            <Button
              variant="secondary"
              onClick={() => indexMutation.mutate()}
              loading={indexMutation.isPending}
              disabled={!settings?.enabled}
            >
              Reindexează arhiva
            </Button>
          )}
          {isAdmin && (
            <Button variant="secondary" onClick={() => setShowSettings((s) => !s)}>
              {showSettings ? 'Ascunde setările' : 'Setări Nextcloud'}
            </Button>
          )}
        </div>
      </div>

      <Card>
        <div className="text-xs text-b4l-text-muted">
          {settings ? (
            <>
              <strong>Stare:</strong> {settings.enabled ? '✓ Activ' : '✗ Inactiv'} ·{' '}
              <strong>Parolă:</strong> {settings.passwordSet ? '✓' : '✗'} · <strong>Folder:</strong>{' '}
              <code>{settings.rootPath}</code> · <strong>Ultima indexare:</strong>{' '}
              {lastSync
                ? `${lastSync.toLocaleString('en-GB')} (${settings.lastSyncCount ?? 0} fișiere)`
                : 'niciodată'}
            </>
          ) : (
            'Setările Nextcloud nu sunt configurate.'
          )}
        </div>
      </Card>

      {isAdmin && showSettings && (
        <Card
          title="Configurare Nextcloud"
          description="Date de conectare WebDAV pentru arhiva de bannere."
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field
              label="Adresă WebDAV"
              placeholder="https://nx59150.your-storageshare.de"
              value={s_webdavUrl}
              onChange={(e) => setSWebdavUrl(e.target.value)}
            />
            <Field
              label="Nume de utilizator"
              placeholder="ex: Victor"
              value={s_username}
              onChange={(e) => setSUsername(e.target.value)}
            />
            <PasswordField
              label={settings?.passwordSet ? 'Parolă nouă (lasă gol pentru a o păstra)' : 'Parolă'}
              value={s_password}
              onChange={(e) => setSPassword(e.target.value)}
            />
            <Field
              label="Folder bannere (de scanat)"
              value={s_rootPath}
              onChange={(e) => setSRootPath(e.target.value)}
              hint="Ex: 04_Marketing ecosystem (sau orice subfolder)"
            />
            <Field
              label="Link public Nextcloud pentru deconturi"
              placeholder="https://...your-storageshare.de/s/TOKEN"
              value={s_deconturiShareToken}
              onChange={(e) => setSDeconturiShareToken(e.target.value)}
              hint="Lasă gol dacă folderul e pe contul WebDAV. Lipește linkul /s/... dacă e partajat ca public share."
            />
            <Field
              label="Cale deconturi (în share/cont)"
              value={s_deconturiBasePath}
              onChange={(e) => setSDeconturiBasePath(e.target.value)}
              hint="Folderul cu deconturile, relativ la rădăcina share-ului/contului. Ex: DECONTURI"
            />
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={s_enabled}
              onChange={(e) => setSEnabled(e.target.checked)}
            />
            <span>Activ (reindexare automată zilnic, la 03:00)</span>
          </label>
          <div className="mt-3 flex gap-2">
            <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
              Salvează
            </Button>
            <Button
              variant="secondary"
              onClick={() => testMutation.mutate()}
              loading={testMutation.isPending}
              disabled={!settings?.passwordSet}
            >
              Testează conexiunea
            </Button>
          </div>
        </Card>
      )}

      <Card title="Căutare bannere">
        {dimensionsQuery.data && dimensionsQuery.data.dimensions.length > 0 && (
          <div className="mb-3">
            <div className="mb-1 text-xs font-medium text-b4l-text-dim">
              Dimensiuni găsite în arhivă (apasă ca să filtrezi):
            </div>
            <div className="flex flex-wrap gap-1">
              {dimensionsQuery.data.dimensions.slice(0, 30).map((d) => {
                const active = String(d.width) === width && String(d.height) === height
                return (
                  <button
                    key={`${d.width}x${d.height}`}
                    onClick={() => {
                      setWidth(String(d.width))
                      setHeight(String(d.height))
                    }}
                    className={`rounded border px-2 py-0.5 font-mono text-[11px] ${
                      active
                        ? 'border-b4l-blue bg-b4l-blue text-white'
                        : 'border-b4l-border bg-white hover:bg-b4l-bg-soft'
                    }`}
                  >
                    {d.width}×{d.height} <span className="opacity-60">({d.count})</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-7">
          <Field
            label="Lățime (px)"
            placeholder="2280"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
          />
          <Field
            label="Înălțime (px)"
            placeholder="660"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
          />
          <Field
            label="Toleranță (±px)"
            placeholder="0"
            value={tolerance}
            onChange={(e) => setTolerance(e.target.value)}
            hint="Ex: 50 pentru ±50px"
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-b4l-text-dim">Produs</label>
            <select
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
            >
              {PRODUCTS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <Field
            label="Cuvânt cheie"
            placeholder="ex: drmax, promo, header"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-b4l-text-dim">Tip fișier</label>
            <select
              value={ext}
              onChange={(e) => setExt(e.target.value)}
              className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
            >
              <option value="">Toate</option>
              <option value="jpg">JPG</option>
              <option value="png">PNG</option>
              <option value="webp">WEBP</option>
              <option value="ai">AI (sursă)</option>
              <option value="psd">PSD (sursă)</option>
              <option value="pdf">PDF</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-b4l-text-dim">Etichetă</label>
            <select
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
            >
              <option value="">—</option>
              <option value="banner">banner</option>
              <option value="header">header</option>
              <option value="mobile">mobile</option>
              <option value="desktop">desktop</option>
              <option value="social">social</option>
              <option value="story">story</option>
              <option value="post">post</option>
              <option value="promo">promo</option>
              <option value="campaign">campanie</option>
              <option value="drmax">drmax</option>
              <option value="emag">emag</option>
              <option value="shopify">shopify</option>
            </select>
          </div>
        </div>
      </Card>

      <Card title={`Rezultate (${searchQuery.data?.total ?? 0} fișiere)`}>
        {searchQuery.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
        {searchQuery.data?.files.length === 0 && (
          <p className="text-sm text-b4l-text-muted">
            Niciun fișier găsit. Lărgește filtrele sau mărește toleranța.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {searchQuery.data?.files.map((f) => (
            <BannerCard key={f.id} file={f} />
          ))}
        </div>
      </Card>
    </div>
  )
}

function BannerCard({ file }: { file: BannerFile }) {
  const isImage = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(file.ext)
  const sizeMb = (Number(file.sizeBytes) / 1024 / 1024).toFixed(1)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [errored, setErrored] = useState(false)

  // Lazy load thumbnail via axios (cu auth header) si converteste la blob URL
  useEffect(() => {
    if (!isImage || blobUrl || errored) return
    let cancelled = false
    let createdUrl: string | null = null
    setLoading(true)
    api
      .get(`/marketing/banner-archive/file/${file.id}/raw`, { responseType: 'blob' })
      .then((resp) => {
        if (cancelled) return
        const url = URL.createObjectURL(resp.data as Blob)
        createdUrl = url
        setBlobUrl(url)
      })
      .catch(() => {
        if (!cancelled) setErrored(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [file.id, isImage, blobUrl, errored])

  // Click pe card: deschide imaginea (sau descarca) intr-o tab noua
  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    try {
      const resp = await api.get(`/marketing/banner-archive/file/${file.id}/raw`, {
        responseType: 'blob',
      })
      const url = URL.createObjectURL(resp.data as Blob)
      window.open(url, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      toast.error('Fișierul nu a putut fi deschis. Încearcă din nou.')
    }
  }

  return (
    <a
      href="#"
      onClick={onClick}
      className="group block overflow-hidden rounded border border-b4l-border bg-white transition hover:border-b4l-blue"
    >
      <div className="aspect-square w-full overflow-hidden bg-b4l-bg-soft">
        {isImage ? (
          loading && !blobUrl ? (
            <div className="flex h-full w-full items-center justify-center text-xs text-b4l-text-muted">
              Se încarcă…
            </div>
          ) : errored ? (
            <div className="flex h-full w-full items-center justify-center text-xs text-b4l-red-dark">
              Eroare
            </div>
          ) : blobUrl ? (
            <img
              src={blobUrl}
              alt={file.filename}
              loading="lazy"
              className="h-full w-full object-contain"
            />
          ) : null
        ) : (
          <div className="flex h-full w-full items-center justify-center text-2xl font-mono uppercase text-b4l-text-muted">
            .{file.ext}
          </div>
        )}
      </div>
      <div className="p-2 text-[11px]">
        <div className="truncate font-medium" title={file.filename}>
          {file.filename}
        </div>
        <div className="text-b4l-text-muted">
          {file.parsedWidth && file.parsedHeight ? (
            <span className="font-mono">
              {file.parsedWidth}×{file.parsedHeight}
            </span>
          ) : (
            '—'
          )}
          {' · '}
          {sizeMb} MB
        </div>
        <div className="truncate text-b4l-text-muted" title={file.path}>
          {file.path}
        </div>
        {file.parsedTags && file.parsedTags.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {file.parsedTags.slice(0, 3).map((t) => (
              <span key={t} className="rounded bg-b4l-bg-soft px-1 text-[10px]">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </a>
  )
}
